// Lot 680 — Avatar robuste (même API que shadcn/Radix : Avatar / AvatarImage / AvatarFallback), sans Radix.
// Pourquoi : Radix n'affichait la photo qu'une fois ENTIÈREMENT chargée (lettre pendant tout le chargement sur
// réseau lent, et à chaque remontage d'une publication par le fil fenêtré du lot 677) et, si la variante `_w128`
// manquait sur le CDN, restait en lettre pour toujours sans réessayer. Ici :
//   • la lettre (AvatarFallback) est toujours rendue DESSOUS ; la photo la recouvre dès ses premiers pixels ;
//   • en cas d'échec, on retente l'original de la photo (imageVariantFallback, comme les photos du fil — lot 420) ;
//   • si l'original échoue aussi, l'image est cachée → la lettre reste visible (jamais d'icône « image cassée »).
import * as React from "react";

import { cn } from "@/lib/utils";
import { imageVariantFallback } from "@/lib/mediaUrl";

// `isolate` : la photo porte z-[1] (au-dessus de la lettre) — sans contexte d'empilement propre, ce z-index « fuyait »
// et recouvrait les voisins positionnés (bouton caméra du profil, point vert, badge LIVE).
const Avatar = React.forwardRef(({ className, ...props }, ref) => (
  <span ref={ref} className={cn("relative isolate flex h-10 w-10 shrink-0 overflow-hidden rounded-full", className)} {...props} />
));
Avatar.displayName = "Avatar";

const AvatarImage = React.forwardRef(({ className, src, alt = "", onError, ...props }, ref) => {
  const [failed, setFailed] = React.useState(false);
  const errors = React.useRef(0);
  // Nouvelle source (changement d'auteur, nouvelle photo) → on retente l'affichage.
  React.useEffect(() => { setFailed(false); errors.current = 0; }, [src]);
  const handleError = (e) => {
    onError?.(e);
    errors.current += 1;
    const el = e.currentTarget;
    // 1er échec : l'écouteur global de mediaUrl (phase de capture) a généralement DÉJÀ basculé variante → original
    // (src DOM ≠ prop) ; sinon on le fait ici. Dans les deux cas l'original se charge : on garde l'image.
    if (errors.current === 1 && (el.getAttribute("src") !== src || imageVariantFallback(el))) return;
    // 2e échec (l'original aussi) : on cache l'image, la lettre dessous reste — jamais d'icône « image cassée ».
    setFailed(true);
  };
  if (!src || failed) return null;
  return (
    <img
      ref={ref}
      src={src}
      alt={alt}
      draggable={false}
      decoding="async"
      onError={handleError}
      className={cn("relative z-[1] aspect-square h-full w-full object-cover", className)}
      {...props}
    />
  );
});
AvatarImage.displayName = "AvatarImage";

const AvatarFallback = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn("absolute inset-0 flex h-full w-full items-center justify-center rounded-full bg-muted", className)}
    {...props}
  />
));
AvatarFallback.displayName = "AvatarFallback";

export { Avatar, AvatarImage, AvatarFallback };
